import { createSignal } from '@funtech-int/rival';
import {
    useRef,
    useEffect,
    useId,
    type ReactNode,
    type RefObject,
    type KeyboardEvent,
    useMemo,
} from 'react';

interface SelectOption {
    value: string;
    label: string;
    icon?: ReactNode;
}

interface UseSelectProps {
    options: SelectOption[];
    value: string | null;
    onChange: (value: string | null) => void;
    disabled?: boolean;
}

interface UseSelect {
    isOpenSignal: ReturnType<typeof createSignal<boolean>>;
    highlightedIndexSignal: ReturnType<typeof createSignal<number>>;
    selectedOption: SelectOption | undefined;
    selectRef: RefObject<HTMLDivElement>;
    listboxId: string;
    optionId: (i: number) => string;
    handleKeyDown: (e: KeyboardEvent) => void;
    toggleOpen: () => void;
    setHighlightedIndex: (index: number) => void;
}

export const useSelect = ({
    options,
    value,
    onChange,
    disabled = false,
}: UseSelectProps): UseSelect => {
    const selectRef = useRef<HTMLDivElement>(null);

    const listboxId = useId();

    const selectedOption = useMemo(
        () => options.find((opt) => opt.value === value),
        [options, value]
    );

    const { isOpenSignal, highlightedIndexSignal, optionId, handleKeyDown, toggleOpen } =
        useMemo(() => {
            const isOpenSignal = createSignal(false);
            const highlightedIndexSignal = createSignal(-1);

            const optionId = (i: number) => `${listboxId}-opt-${i}`;

            const handleKeyDown = (e: KeyboardEvent) => {
                if (disabled) return;

                const currentIsOpen = isOpenSignal.get();
                const currentHighlightedIndex = highlightedIndexSignal.get();

                switch (e.key) {
                    case 'Enter':
                    case ' ':
                        e.preventDefault();
                        if (currentIsOpen) {
                            if (currentHighlightedIndex >= 0) {
                                onChange(options[currentHighlightedIndex]?.value ?? null);
                            }
                            isOpenSignal.set(false);
                            highlightedIndexSignal.set(-1);
                        } else {
                            isOpenSignal.set(true);
                            highlightedIndexSignal.set(-1);
                        }
                        break;

                    case 'ArrowDown':
                        e.preventDefault();
                        if (!currentIsOpen) {
                            isOpenSignal.set(true);
                        } else if (options.length > 0) {
                            highlightedIndexSignal.set((prev) => {
                                const next = prev < 0 ? 0 : (prev + 1) % options.length;
                                return next;
                            });
                        }
                        break;

                    case 'ArrowUp':
                        e.preventDefault();
                        if (!currentIsOpen) {
                            isOpenSignal.set(true);
                        } else if (options.length > 0) {
                            highlightedIndexSignal.set((prev) => {
                                const next =
                                    prev < 0
                                        ? options.length - 1
                                        : (prev - 1 + options.length) % options.length;
                                return next;
                            });
                        }
                        break;

                    case 'Escape':
                        e.preventDefault();
                        isOpenSignal.set(false);
                        highlightedIndexSignal.set(-1);
                        break;

                    case 'Tab':
                        isOpenSignal.set(false);
                        highlightedIndexSignal.set(-1);
                        break;

                    default:
                        break;
                }
            };

            const toggleOpen = () => {
                if (disabled) return;
                isOpenSignal.set((prev) => {
                    if (prev) {
                        highlightedIndexSignal.set(-1);
                    }

                    return !prev;
                });
                highlightedIndexSignal.set(-1);
            };

            return { isOpenSignal, highlightedIndexSignal, optionId, handleKeyDown, toggleOpen };
        }, [disabled, listboxId, onChange, options]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
                isOpenSignal.set(false);
                highlightedIndexSignal.set(-1);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleIsOpenChange = () => {
            const isOpen = isOpenSignal.get();

            if (!isOpen) {
                highlightedIndexSignal.set(-1);
                return;
            }

            if (options.length === 0) {
                highlightedIndexSignal.set(-1);
                return;
            }

            const selectedIndex = options.findIndex((opt) => opt.value === value);

            highlightedIndexSignal.set(selectedIndex >= 0 ? selectedIndex : 0);
        };

        handleIsOpenChange();

        const unsubscribe = isOpenSignal.listen(handleIsOpenChange);

        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, value]);

    useEffect(() => {
        const updateAriaActivedescendant = () => {
            if (!selectRef.current) return;

            const highlightedIndex = highlightedIndexSignal.get();
            const isCurrentlyOpen = isOpenSignal.get();
            const newActivedescendantId =
                isCurrentlyOpen && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined;

            if (newActivedescendantId) {
                selectRef.current.setAttribute('aria-activedescendant', newActivedescendantId);
            } else {
                selectRef.current.removeAttribute('aria-activedescendant');
            }
        };

        updateAriaActivedescendant();

        const unsubscribeHighlightedIndex = highlightedIndexSignal.listen(
            updateAriaActivedescendant
        );

        const unsubscribeIsOpen = isOpenSignal.listen(updateAriaActivedescendant);

        return () => {
            unsubscribeHighlightedIndex();
            unsubscribeIsOpen();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionId]);

    return {
        isOpenSignal,
        highlightedIndexSignal,
        selectedOption,
        selectRef,
        listboxId,
        optionId,
        handleKeyDown,
        toggleOpen,
        setHighlightedIndex: highlightedIndexSignal.set,
    };
};
